import {
  AccountFlagEnum,
  AccountPlanEnum,
  useAccountBillingInfoQuery,
} from '~/generated/graphql';

import { useCreditUsage } from './useCreditUsage';
import useCurrentAccount from './useCurrentAccount';

/**
 * Legacy plans that include enrichment and advertising, etc
 * Currently not taking new customers
 */
export const legacyPlans = [AccountPlanEnum.FreeTier, AccountPlanEnum.PaidTier];

/**
 * Sales intelligence plans
 */
export const salesIntelligencePlans = [
  AccountPlanEnum.HubspotFreeTier,
  AccountPlanEnum.HubspotPaidTier,
];

/**
 * Hubspot platform plans
 */
export const hubspotPlatformPlans = [
  AccountPlanEnum.HubspotPlatformFree,
  AccountPlanEnum.HubspotPlatformPaid,
];

export const selfServePlans = [...salesIntelligencePlans, ...legacyPlans];

export const contractPlans = [AccountPlanEnum.BusinessTier];

export const useEntitlements = () => {
  const { account, currentUser, loading: loadingAccount } = useCurrentAccount();
  const { data: billingInfoData } = useAccountBillingInfoQuery();
  const { hasUsage, loading: loadingUsage } = useCreditUsage();

  const hasFlag = (flag: AccountFlagEnum) => !!account?.flags?.includes(flag);
  const hasPlans = (plans: AccountPlanEnum[]) =>
    !!account?.plans?.some((plan) => plans.includes(plan)) && !loadingAccount;

  const isLoading = loadingAccount || loadingUsage;
  const isOwner = currentUser?.role === 'owner';

  const isContractAccount = hasPlans(contractPlans);
  const isHubspotPlatform = hasPlans(hubspotPlatformPlans);
  const isSalesIntelligence = hasPlans(salesIntelligencePlans);
  const isHubspotAccount = isSalesIntelligence || isHubspotPlatform;
  const isLegacyAccount = !isHubspotAccount && !loadingAccount;
  const isSelfServeAccount = hasPlans(selfServePlans);

  /**
   * Add new entitlements in alphabetical order to keep the list in order
   * and make it easier to find the entitlements.
   *
   * Use entitlements in AppRoutes.tsx and NavBar.tsx to show/hide routes and
   * nav items respectively. Can also use entitlements in components to show/hide
   * content.
   *
   * @example canAccessAdvertising
   */
  const hasAccessAdvertising =
    isLegacyAccount && hasFlag(AccountFlagEnum.AllowPlatformAdvertisingMenu);
  const hasAccessBatch = !isHubspotPlatform;
  const hasAccessBilling = isOwner && !!billingInfoData?.accountBillingInfo;

  const hasAccessCompanies = isLegacyAccount;
  const hasAccessEnrichment = isLegacyAccount;
  const hasAccessEnrichmentHubspot = isHubspotPlatform;
  const hasAccessEnrichmentHubspotEditing = hasPlans([
    AccountPlanEnum.HubspotPlatformPaid,
  ]);
  const hasAccessForms = isLegacyAccount;
  const hasAccessFormsHubspot = isHubspotAccount;
  const hasAccessIntegrations = isLegacyAccount;
  const hasAccessLegacyRevealPlan =
    isLegacyAccount && hasPlans([AccountPlanEnum.Ipcompany]);
  const hasAccessLookup =
    isHubspotPlatform || isLegacyAccount || isSalesIntelligence;

  const hasAccessPeople = isLegacyAccount;

  const hasAccessPlans = hasPlans(selfServePlans);

  const hasAccessProspector =
    (isSelfServeAccount || isContractAccount) && !isHubspotPlatform;
  const hasAccessProspectorCRMExports = isHubspotAccount || isContractAccount;
  const hasAccessProspectorExports = hasUsage;

  const hasAccessSettingsApi = isLegacyAccount;
  const hasAccessSettingsApiUsage =
    hasAccessSettingsApi && hasFlag(AccountFlagEnum.AllowApiUsageTab);
  const hasAccessSettingsIntegrations = isHubspotAccount;
  const hasAccessSettingsSecurity = isLegacyAccount;
  const hasAccessSettingsTeamCreation = isLegacyAccount;
  const hasAccessSettingsTeams = isLegacyAccount;
  const hasAccessSettingsTeamUsers = isLegacyAccount;

  const hasAccessUsage = hasUsage;

  return {
    hasAccessAdvertising,
    hasAccessBatch,
    hasAccessBilling,
    hasAccessCompanies,
    hasAccessSettingsIntegrations,
    hasAccessEnrichment,
    hasAccessEnrichmentHubspot,
    hasAccessEnrichmentHubspotEditing,
    hasAccessForms,
    hasAccessFormsHubspot,
    hasAccessIntegrations,
    hasAccessLegacyRevealPlan,
    hasAccessLookup,
    hasAccessPlans,
    hasAccessPeople,
    hasAccessProspector,
    hasAccessProspectorCRMExports,
    hasAccessProspectorExports,
    hasAccessSettingsApi,
    hasAccessSettingsApiUsage,
    hasAccessSettingsSecurity,
    hasAccessSettingsTeamCreation,
    hasAccessSettingsTeams,
    hasAccessSettingsTeamUsers,
    hasAccessUsage,
    isOwner,
    loading: isLoading,
  };
};
